import { navigate as navigateRoute } from '@reach/router'
import qs from 'query-string'
import { startsWith, isEmpty } from 'lodash-es'
import {
  PRODUCT_NAME_VARIATIONS,
  getProductSystemName,
  getSupportedCountryCodeByProductName,
  PRODUCT_NAMES,
} from 'universal/cfs-card'
import { FPTI_COMMON_BUTTONS, isReturnUriToNative, FLOWS } from 'universal/ucs'
import {
  getCardManagementPath,
  PP_SUMMARY,
  BB_PPCC_HOME,
  UCS_BASE_PATH,
  FUNDS_TRANSFER_TO_BALANCE_PATH,
} from 'universal/routes'
import { ADDRESS_TYPES } from 'universal/address'
import { ENROLLMENT_INTENTS, INTENT_TYPES } from 'universal/constants'
import { getQueryParam, getQueryParams, appendQueryParams } from 'utils/query'
import {
  isNewFlowModal,
  createSetupFIFlowUrl,
  createChangeAddressFlowUrl,
  isFullPageLayout,
  createShowCardFlowUrl,
} from 'utils/flow'
import { getProductQueryParam } from 'utils/utils'
import { hasFeature, serverData, getPayPalUrl } from './server-data'
import { VeniceHandlerName, isMobileNative, triggerVeniceCallback } from './mobile'
import * as fpti from './fpti'
import { showModalPageSpinner, showMainPageSpinner } from '@/features/ui/ui-slice'
import { FLOW_STEPS } from '@/constants/flow-steps'
import { ACCOUNT_TYPES } from '@/constants/common'
import { FLOW_PATHS } from '@/constants/flow'
import { CLIENT_ROUTE_PATHS } from 'utils/routes'

const { STATIC_PAGE: STATIC_PAGE_ROUTE_PATH, PRODUCT_STATIC_PAGE: PRODUCT_STATIC_PAGE_ROUTE_PATH } =
  CLIENT_ROUTE_PATHS

const { REPLACE_CARD, ENROLL_CARD } = FLOWS
const { CONSUMER_DEBIT_CARD } = PRODUCT_NAMES
const { BUSINESS, PREMIER, PERSONAL } = ACCOUNT_TYPES

function navigate(path = '', options: any = {}) {
  // 'replace' option is used to prevent new history entry
  // therefore, the webview container back button will be disabled
  // and the user is required to use the flow modal back button
  // e.g. between 2 flows - activate card flow to replace card flow
  // Prefer live isMobileNative() over sticky serverData (Venice/PayPalQA add-address exits).
  if (isMobileNative() && isNewFlowModal()) {
    options.replace = true
  }

  navigateRoute(path, options)
}

/**
 * Between internal flows, React.Suspense will render the ModalShell as fallback.
 * However, on stage, css bundles take a long time to be fetched.
 * This causes a loading spinner without an overlay or text to be rendered BEFORE the ModalShell fallback.
 *
 * @param {*} path - To internal flows
 * @param {*} options.dispatch - To display page spinner before the fallback to ModalShell
 */
export function goTo(path, options: any = {}) {
  const { dispatch } = options

  if (dispatch) {
    dispatch(showModalPageSpinner())
  }

  const basePath = serverData.requestURI
  navigate(`${basePath}${path || ''}`)
}

export function goBack(e) {
  e?.preventDefault()
  window.history.back()
}

export function returnToNative(webviewPayload) {
  const webviewCallback = getQueryParam('webviewCallback') || VeniceHandlerName.close
  triggerVeniceCallback(webviewCallback, webviewPayload)
}

export function isBlockedUri(uri) {
  const myAccountRegEx = new RegExp('^/myaccount')

  return !uri || !startsWith(uri, '/') || uri.startsWith('//') || !myAccountRegEx.test(uri)
}

export function goToUriMechanism(uriParamName, dispatch, { urlParams }: any = {}) {
  // Allow only uriParamValue, default to PP_SUMMARY
  let uriParamValue = getQueryParam(uriParamName) || PP_SUMMARY

  // Append query Params to track where they source from.
  if (!isEmpty(urlParams)) {
    uriParamValue = appendQueryParams(uriParamValue, urlParams)
  }

  const basePath = serverData.requestURI
  const originLayout = isFullPageLayout(window.location.pathname)
  const destinationLayout = isFullPageLayout(uriParamValue)

  if (!startsWith(uriParamValue, basePath) || originLayout !== destinationLayout) {
    secureRedirectTo(uriParamValue, { dispatch })
    return
  }
  navigate(uriParamValue)
}

export function goToReturnUri(dispatch, { urlParams }: any = {}) {
  goToUriMechanism('returnUri', dispatch, { urlParams })
}

export function returnToOrigin(dispatch, options: any = {}) {
  const { webviewPayload, endFlow } = options

  // For webview request
  const webviewCallback = getQueryParam('webviewCallback')

  if (isMobileNative()) {
    // Only trigger the Venice callback if a payload is provided.
    // This prevents unexpected behavior if a callback is triggered,
    // but no data is intended to be sent back to the native app.
    if (webviewCallback && webviewPayload) {
      triggerVeniceCallback(webviewCallback, webviewPayload)
      return
    }
    if (endFlow) {
      // Venice implements venice.close. OSLO SecureWebView does not — it dismisses when the
      // webview navigates to /nativeReturnUri (see DebitCardWebViewScreen). Falling through
      // after a no-op close left BDMC /address/add blank after gate failures / cancel.
      const closed = (triggerVeniceCallback as any)(VeniceHandlerName.close)
      if (!closed) {
        browserRedirectTo('/nativeReturnUri')
      }
      return
    }
  }

  goToReturnUri(dispatch, options)
}

export function returnToOriginHandler(dispatch, options) {
  return () => {
    returnToOrigin(dispatch, options)
  }
}

/**
 * To reset the page to empty page, so that existing page is not displayed again
 * after the fetch is completed, path changes but actual redirection does not
 * happen with navigate call
 *
 * @param options.dispath - To display spinner for the new flow modal for better
 *     user experience
 */
export function redirectTo(path, options: any = {}) {
  const { dispatch } = options

  if (dispatch) {
    // Display spinner while new page is being loaded, better experience
    dispatch(showModalPageSpinner())
    dispatch(showMainPageSpinner())
  } else {
    const { updateUrlBeforeRedirect = false } = options
    // Remove flow modal just display empty page, more responseive to the user
    // as the new page is being loaded, but default background colour is blue,
    // can be distraacting if you are not redirecting back to the summary page
    //
    // TODO If this is causing more UX issue, for other redirects, this addional
    // step should probably be removed
    if (updateUrlBeforeRedirect) {
      navigate(path)
    }
  }

  browserRedirectTo(path)
}

export function secureRedirectTo(path, options: any = {}) {
  const validatedPath = isBlockedUri(path) ? PP_SUMMARY : path
  redirectTo(validatedPath, options)
}

export function openNewTab(path) {
  window.open(path, '_blank', 'noreferrer')
}

// TODO May need to support multiple query params in the future.
// Currently supports propagation of entryPoint query param
// Should always *not* support propagation of showKey query param (unless its handling is improved)
export function goToSummary(productName?, entryPoint?) {
  if (productName) {
    let productSummaryUrl =
      serverData.requestURI + '/search' + '?product_name=' + getProductSystemName(productName)

    if (entryPoint && entryPoint.entryPointName) {
      productSummaryUrl += '&entrypoint=' + entryPoint.entryPointName
    }
    navigate(productSummaryUrl)
  } else {
    navigate(serverData.requestURI + '/search')
  }
}

function onFlowCompleteHandler({ redirectPath }, options?) {
  if (isMobileNative()) {
    // Match returnToOrigin endFlow: OSLO has no venice.close — dismiss via native return URI.
    const closed = (triggerVeniceCallback as any)(VeniceHandlerName.close)
    if (!closed) {
      browserRedirectTo('/nativeReturnUri')
    }
    return
  }
  redirectTo(redirectPath, options)
}

export function goToMyAccount(options = {}) {
  onFlowCompleteHandler({ redirectPath: '/myaccount' }, options)
}

export function goToCardsHome() {
  onFlowCompleteHandler({ redirectPath: BB_PPCC_HOME })
}

export function goToContactUs() {
  const country = serverData.locality?.country?.toLowerCase()
  browserRedirectTo(getPayPalUrl(`${country}/smarthelp/contact-us`))
}

export function goToPayPalUserAgreement() {
  const currentCountry = serverData.locality?.country?.toLowerCase()
  const countryCode = getSupportedCountryCodeByProductName(CONSUMER_DEBIT_CARD, currentCountry)
  browserRedirectTo(getPayPalUrl(`${countryCode}/legalhub/pp-balance-tnc#holding`))
}

export function goToEnrollmentForVirtualCard(entryPoint) {
  if (!hasFeature('enableVirtualCardEnrollment')) {
    goToMyAccount()
    return null
  }

  const entryPointParam =
    entryPoint && entryPoint.entryPointName ? '?entrypoint=' + entryPoint.entryPointName : ''
  const enrollmentUrl =
    serverData.requestURI + '/enroll/' + PRODUCT_NAME_VARIATIONS.vc.userFriendly + entryPointParam
  navigate(enrollmentUrl)
  return null
}

export function getFlowEntryExitProps(fptiProps: any = {}, entryPoint) {
  const entryOrExit = () => {
    if (entryPoint && entryPoint.entryPointUri) {
      window.location = entryPoint.entryPointUri
    } else {
      goToMyAccount()
    }
  }

  const { fptiPage, ...tags } = fptiProps
  const entryOrExitWithTracking = (page, fptiLink) => {
    fpti.recordClick(page || fptiPage, fptiLink, tags)
    entryOrExit()
  }

  return {
    onDone: entryOrExitWithTracking,
    onBack: () => entryOrExitWithTracking(null, FPTI_COMMON_BUTTONS.BACK),
    onClose: () => entryOrExitWithTracking(null, FPTI_COMMON_BUTTONS.CLOSE),
  }
}

export function shouldAndDidTriggerCallbackViaReturnUri(returnUri) {
  if (isMobileNative() && isReturnUriToNative(returnUri)) {
    return (triggerVeniceCallback as any)(VeniceHandlerName.close)
  }
  return false
}

export function goToReturnUriHandler(dispatch, options) {
  return () => {
    goToReturnUri(dispatch, options)
  }
}

export function goToMyAccountWithSpinnerHandler(dispatch) {
  return () => {
    const basePath = serverData.requestURI
    if (!startsWith(PP_SUMMARY, basePath)) {
      redirectTo(PP_SUMMARY, { dispatch })
      return
    }
    navigate(PP_SUMMARY)
  }
}

export function onFirstStepBackHandler(dispatch?: any, options: any = {}) {
  return getQueryParam('returnUri') ? goToReturnUriHandler(dispatch, options) : null
}

export function isReturnAndCancelUriExist() {
  const { returnUri, cancelUri } = getQueryParams() as any
  return !!returnUri && !!cancelUri
}

export function onFlowModalBackHandler(dispatch?: any, options: any = {}) {
  if (isReturnAndCancelUriExist()) {
    return goToReturnUriHandler(dispatch, options)
  }

  const nullToHideBackButton = null

  return nullToHideBackButton
}

export function goToCancelUri(dispatch?: any, options: any = {}) {
  goToUriMechanism('cancelUri', dispatch, options)
}

export function goToCancelUriHandler(dispatch, options = {}) {
  return () => {
    goToCancelUri(dispatch, options)
  }
}

export function onFlowModalCloseHandler(dispatch?: any, options: any = {}) {
  const { returnUri, cancelUri } = getQueryParams() as any

  if (cancelUri) {
    return goToCancelUriHandler(dispatch, options)
  }

  if (returnUri) {
    return goToReturnUriHandler(dispatch, options)
  }

  return (goToMyAccountWithSpinnerHandler as any)(dispatch, options)
}

export function goToSetupFIFlow({
  flowName,
  dispatch,
  productName,
  additionalReturnUriQueryParameters = {},
}) {
  if (!productName) return

  const { ...otherParams } = qs.parse(window.location.search)

  let cancelUri = otherParams.returnUri || PP_SUMMARY

  const convertedProductName = getProductQueryParam(productName)

  const defaultReturnUriQueryParameters = {
    productName: convertedProductName,
  }

  const returnUriQueryParameters = generateReturnUriQueryParameters()

  const returnUriQueryString = qs.stringify({
    ...otherParams,
    ...defaultReturnUriQueryParameters,
    ...returnUriQueryParameters,
    ...additionalReturnUriQueryParameters,
  })

  const returnUri = window.location.pathname + `?${returnUriQueryString}`

  const defaultQueryParameters = {
    productName: convertedProductName,
    returnUri,
    cancelUri,
  }

  const queryParameters = {
    intent: INTENT_TYPES.ENROLL,
  }

  const setupFIFlowUrl = (createSetupFIFlowUrl as any)({
    ...defaultQueryParameters,
    ...queryParameters,
  })

  if (!setupFIFlowUrl) {
    return
  }
  // Go to "/setup-fi"
  goTo(setupFIFlowUrl, { dispatch })

  function generateReturnUriQueryParameters() {
    if (flowName === ENROLL_CARD) {
      return {
        page: FLOW_STEPS.ENROLL_CARD.CARD_ENROLLING_STEP,
      }
    }
    return {}
  }
}

export function goToChangeAddressFlow({
  flowName,
  dispatch,
  reason,
  productName,
  cardId,
  shippingAddress,
  addressType = ADDRESS_TYPES.SHIPPING,
  skipAddressSelection,
  variant,
  additionalReturnUriQueryParameters = {},
  redirectToCardManagementOnClose = false,
}) {
  if (!productName) return

  /* eslint-disable no-unused-vars */
  const {
    addressId,
    status: cipStatus,
    statusCode: cipStatusCode,
    ...otherParams
  } = qs.parse(window.location.search)

  let cancelUri = otherParams.returnUri || PP_SUMMARY // cancelUri will set the redirect Url when close button is clicked in Change Address Flow
  if (otherParams.intent === ENROLLMENT_INTENTS.PAYROLL_PARTNER) {
    cancelUri = otherParams.cancelUri
  } else if (redirectToCardManagementOnClose) {
    cancelUri = getCardManagementPath({ productName })
  }

  const convertedProductName = getProductQueryParam(productName)

  const defaultReturnUriQueryParameters = {
    ...otherParams,
    productName: convertedProductName,
  }
  const returnUriQueryParameters = generateReturnUriQueryParameters()
  const returnUriQueryString = qs.stringify({
    ...otherParams,
    ...defaultReturnUriQueryParameters,
    ...returnUriQueryParameters,
    ...additionalReturnUriQueryParameters,
  })

  const returnUri = window.location.pathname + `?${returnUriQueryString}`

  const defaultQueryParameters = {
    productName: convertedProductName,
    addressType,
    returnUri,
    cancelUri,
  }

  const queryParameters = generateQueryParameters()
  const changeAddressFlowUrl = (createChangeAddressFlowUrl as any)({
    ...defaultQueryParameters,
    ...queryParameters,
  })

  if (!changeAddressFlowUrl) {
    return
  }

  goTo(changeAddressFlowUrl, { dispatch })

  function generateReturnUriQueryParameters() {
    switch (flowName) {
      case REPLACE_CARD:
        return {
          reason,
        }
      case ENROLL_CARD:
        return {
          page: FLOW_STEPS.ENROLL_CARD.CARD_ENROLLING_STEP,
        }
      default:
        return {}
    }
  }

  function generateQueryParameters() {
    switch (flowName) {
      case REPLACE_CARD:
        return {
          cardId,
          addressId: addressId || shippingAddress?.id,
          skipAddressSelection,
          // Surface the reason top-level so /address/change can detect the request-physical-card flow
          // (isFlowForRequestPhysicalCard reads it). Without it, BDMC request-physical falls through to
          // the card-address selection UI instead of the "Where we'll ship your card" screen.
          reason,
        }
      case ENROLL_CARD:
        return {
          intent: INTENT_TYPES.ENROLL,
          variant,
          ...(cardId && { cardId }),
        }
      default:
        return {}
    }
  }
}

export const goToCardManagementFlow = (card, queryParameters = {}, options = {}) => {
  const redirectPath = getCardManagementPath(card, queryParameters)
  onFlowCompleteHandler({ redirectPath }, options)
  return
}

export const goToReplaceCardFlow = (cardId, queryParameters = {}) => {
  const searchParams = new URLSearchParams(queryParameters)
  goTo(`/manage/${cardId}/replace?${searchParams}`)
}

export const goToEnrollCardFlowWithSuccess = ({
  productName,
  addressId,
  cardId,
  skipReissue = false,
}) => {
  const queryParameters: any = {
    productName,
    addressId,
    cardId,
    requestPhysicalCard: true,
    intent: 'enroll',
    page: 'cardSuccess',
  }

  if (skipReissue) {
    queryParameters.skipReissue = true
  }

  const enrollUrl = new URL('/myaccount/debitcards/enroll', window.location.origin)
  Object.entries(queryParameters)
    .filter(([, value]) => value !== undefined && value !== null)
    .forEach(([key, value]) => enrollUrl.searchParams.set(key, String(value)))
  window.location.assign(enrollUrl.pathname + enrollUrl.search)
}

export const goToEnrollCardFlow = ({ productName, addressId, cardId, page = 'cardEnrolling' }) => {
  const queryParameters = {
    productName,
    addressId,
    cardId,
    requestPhysicalCard: true,
    intent: 'enroll',
    page,
  }

  const queryString = qs.stringify(queryParameters)
  const enrollCardUrl = `/myaccount/debitcards/enroll?${queryString}`

  window.location.href = enrollCardUrl
}

export const handleGBEnrollmentFlow = ({
  selectedAddressId,
  dispatch,
  fetchName,
  logActivityWithLastImpression,
  successfulFetch,
}) => {
  let cardId = getQueryParam('cardId')
  const productName = getQueryParam('productName') || PRODUCT_NAMES.CONSUMER_DEBIT_CARD

  if (!cardId) {
    const returnUri = getQueryParam('returnUri')
    if (returnUri) {
      const cardIdMatch = returnUri.match(/\/manage\/([^\/]+)\//)
      if (cardIdMatch) {
        cardId = cardIdMatch[1]
      }
    }
  }

  if (cardId && selectedAddressId) {
    goToEnrollCardFlow({
      productName,
      addressId: selectedAddressId,
      cardId,
      page: 'cardEnrolling',
    })

    dispatch(successfulFetch({ fetchName }))

    logActivityWithLastImpression('exitChangeAddressFlow', {
      message: `Redirecting to enroll card flow with cardId: ${cardId}, addressId: ${selectedAddressId}`,
    })

    return true
  }

  const card = { productName }
  goToCardManagementFlow(card, {}, { dispatch })
  return true
}

export const getStaticPageUrl = ({ pageId, productId }) => {
  let staticPageUrl
  const countryCode = serverData.locality.country
  if (productId) {
    staticPageUrl = PRODUCT_STATIC_PAGE_ROUTE_PATH.replace(':productId', productId)
      .replace(':pageId', pageId)
      .replace(':countryCode', countryCode)
  } else {
    staticPageUrl = STATIC_PAGE_ROUTE_PATH.replace(':pageId', pageId).replace(
      ':countryCode',
      countryCode,
    )
  }
  return staticPageUrl
}

export const generateStaticPageUrlWithQueryParameters = ({
  pageId,
  productId,
  queryParams = {},
}) => {
  const staticPageUrl = getStaticPageUrl({ pageId, productId })
  if (staticPageUrl) {
    const queryString = qs.stringify({
      returnUri: window.location.pathname + window.location.search,
      ...queryParams,
    })
    return `${UCS_BASE_PATH}${staticPageUrl}?${queryString}`
  }

  return staticPageUrl
}

export const goToStaticPage = ({ pageId, productId }) => {
  const staticPageUrl = getStaticPageUrl({ pageId, productId })
  goTo(staticPageUrl)
}

export const redirectToStaticPage = ({ pageId, productId }, options) => {
  const staticPageUrl = generateStaticPageUrlWithQueryParameters({ pageId, productId })
  redirectTo(staticPageUrl, options)
}

export function goToPayPalAppRedirect({ dispatch }) {
  const queryString = qs.stringify({
    intent: 'download_mktg',
    src: 'dw_ppcom_homegta_mweb',
    action: 'redirect',
  })
  const appRedirectUrl = getPayPalUrl('cgp/app-redirect')
  redirectTo(`${appRedirectUrl}?${queryString}`, { dispatch })
}

export function goToPayPalAppOnboardingRedirect({ dispatch }, ctxPSrc) {
  const queryString = qs.stringify({
    flow_intent: 'STANDALONE',
    source: 'star_pay',
    ctx_p_src: ctxPSrc,
  })
  const appRedirectUrl = getPayPalUrl('mobile-app/banking-bundle/enroll')
  redirectTo(`${appRedirectUrl}?${queryString}`, { dispatch })
}

export const getShowCardFlowUrl = (card, options: any = {}) => {
  const { returnPath, queryParams, shouldAppendOtherParams = true } = options
  if (!card) return null

  const { cardId = '' } = card

  if (!cardId) return null

  const otherParams = shouldAppendOtherParams ? qs.parse(window.location.search) : {}

  const returnUriQueryString = qs.stringify({
    ...queryParams,
    ...otherParams,
  })

  const returnUri = (returnPath || window.location.pathname) + `?${returnUriQueryString}`

  return createShowCardFlowUrl({
    cardId,
    returnUri,
  })
}

export function browserRedirectTo(url) {
  fpti.recordClientEvent('ucs_redirect_occurred', { redirect_url: url })
  if (typeof url !== 'string') return
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      window.location.assign(parsed.href)
    }
  } catch {
    // Invalid URL; skip redirect
  }
}

/* eslint-disable max-statements */
export function ppdcXOredirectTo(url, options: any = {}) {
  fpti.recordClientEvent('ucs_ppdcxo_stepup_redirect_occurred', { redirect_url: url })
  const { dispatch } = options

  if (dispatch) {
    // Display spinner while new page is being loaded
    dispatch(showModalPageSpinner())
    dispatch(showMainPageSpinner())
  }

  // Ensure trailing slash in authflow path to prevent ANW's 301 redirect
  // which breaks the internal redirect headers in cookieless scenarios
  let stepUpUrlValue = url
  if (url.includes('/authflow/entry') && !url.includes('/authflow/entry/')) {
    stepUpUrlValue = url.replace('/authflow/entry', '/authflow/entry/')
  }

  // Create form for POST to server step-up handler
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `${serverData.requestURI}/pay/stepup?anw_sid=${serverData.nsid}`
  form.style.display = 'none'
  form.target = '_top'

  // Add CSRF token
  const csrfInput = document.createElement('input')
  csrfInput.name = '_csrf'
  csrfInput.value = serverData.csrf || ''
  csrfInput.type = 'hidden'
  form.appendChild(csrfInput)

  // Add NSID (session ID)
  const nsidInput = document.createElement('input')
  nsidInput.name = 'nsid'
  nsidInput.value = serverData.nsid || ''
  nsidInput.type = 'hidden'
  form.appendChild(nsidInput)

  // Add anw_sid for ANW cookie-disabled session tracking
  const anwSidInput = document.createElement('input')
  anwSidInput.name = 'anw_sid'
  anwSidInput.value = serverData.nsid || ''
  anwSidInput.type = 'hidden'
  form.appendChild(anwSidInput)

  // Add step-up URL
  const stepUpUrlInput = document.createElement('input')
  stepUpUrlInput.name = 'stepUpUrl'
  stepUpUrlInput.value = stepUpUrlValue
  stepUpUrlInput.type = 'hidden'
  form.appendChild(stepUpUrlInput)

  // Add access token (from window.ppdcApp)
  const accessTokenInput = document.createElement('input')
  accessTokenInput.name = 'accessToken'
  accessTokenInput.value = window.ppdcApp?.euat || ''
  accessTokenInput.type = 'hidden'
  form.appendChild(accessTokenInput)

  document.body.appendChild(form)
  form.submit()
}

export const goToAddMoneyPage = () => {
  const { accountType } = serverData
  switch (accountType) {
    case BUSINESS:
      redirectTo(FUNDS_TRANSFER_TO_BALANCE_PATH)
      return
    case PREMIER:
      goToStaticPage({ productId: 'business-debit-card', pageId: 'add-money' })
      return
    case PERSONAL:
    default:
      goToStaticPage({ productId: 'paypal-debit-card', pageId: 'add-money' })
  }
}

export const shouldReturnToCardManagement = () => {
  if (isMobileNative()) {
    return false
  }
  const { REQUEST_PHYSICAL_CARD } = FLOW_PATHS
  const returnUri = getQueryParam('returnUri') || ''
  const isReturnUriToCardManagementSubFlow = [REQUEST_PHYSICAL_CARD].some((path) =>
    returnUri.includes(path),
  )
  return isReturnUriToCardManagementSubFlow
}
